/**
 * taahhutnameHtml.js — the small HTML reader behind the project taahhütname PDFs (backlog #666).
 *
 * The wording of a taahhütname is HTML from the Onay Metinleri editor (CKEditor; V78's version 1 is full of Calibri / Times spans, the
 * clean version 2 is not). The PDF needs it as blocks it can lay out itself, so this turns the markup into a flat list:
 *   { tur: "p",  derinlik, parcalar }                  a paragraph (derinlik > 0 = a continuation inside a list item)
 *   { tur: "li", derinlik, isaret, parcalar }          a list item; isaret is "1." (ol) / "•" (ul) / "–" (a nested ul)
 *   { tur: "hr" }                                      <hr /> (splits the text into sections)
 * `parcalar` are runs { metin, kalin, italik } or { satirSonu: true } (a <br>). Only strong / b / em / i matter for the look; span, font, style,
 * colours and sizes are ignored. Tags it does not know stand for their text as a paragraph, exactly as p / div would.
 *
 * Nothing is injected anywhere: the markup is parsed by DOMParser (no script runs) and only text leaves it.
 */

const YOK_SAY = new Set(["script", "style", "head", "title", "template", "noscript"]);
const KALIN = new Set(["strong", "b"]);
const ITALIK = new Set(["em", "i", "cite", "dfn", "var"]);
const SATIR_ICI = new Set([
  "span", "font", "a", "u", "s", "strike", "del", "ins", "sub", "sup", "small", "big", "code", "kbd", "samp", "abbr", "acronym", "mark",
  "label", "time", "q", "bdi", "bdo", "wbr", "tt", "nobr",
]);
const BASLIK_TAG = /^h[1-6]$/;

/** Whitespace a browser would collapse, plus the invisible characters a rich-text editor leaves behind. */
const BOSLUK = /[ \t\r\n\f\v   ]+/g;
const GORUNMEZ = /[​-‍⁠﻿­]/g;

const DOMParserSinifi = () => (typeof DOMParser !== "undefined" ? DOMParser : null);

/** Cleans one run: invisible characters out, every kind of space one plain space. */
function temizle(metin) {
  return String(metin ?? "").replace(GORUNMEZ, "").replace(BOSLUK, " ");
}

/** Joins neighbouring runs of the same style, trims the edges of the block and after each <br>; [] when nothing readable is left. */
function parcalariTopla(ham) {
  const cikti = [];
  for (const p of ham) {
    if (p.satirSonu) { cikti.push({ satirSonu: true }); continue; }
    const metin = temizle(p.metin);
    if (!metin) continue;
    const son = cikti[cikti.length - 1];
    if (son && !son.satirSonu && son.kalin === p.kalin && son.italik === p.italik) son.metin += metin;
    else cikti.push({ metin, kalin: !!p.kalin, italik: !!p.italik });
  }
  // a space at the start of the block or just after a <br> is markup indentation, not text
  let satirBasi = true;
  for (const p of cikti) {
    if (p.satirSonu) { satirBasi = true; continue; }
    if (satirBasi) p.metin = p.metin.replace(/^ +/, "");
    satirBasi = false;
  }
  while (cikti.length && (cikti[cikti.length - 1].satirSonu || !cikti[cikti.length - 1].metin.trim())) cikti.pop();
  if (cikti.length) {
    const son = cikti[cikti.length - 1];
    son.metin = son.metin.replace(/ +$/, "");
  }
  return cikti.filter((p) => p.satirSonu || p.metin);
}

/**
 * The HTML as blocks (see the top of the file). An empty / unreadable text gives [].
 * @param {string} html
 */
export function htmlBloklari(html) {
  const Ayristirici = DOMParserSinifi();
  const kaynak = String(html ?? "");
  if (!kaynak.trim() || !Ayristirici) return [];
  const govde = new Ayristirici().parseFromString(`<!DOCTYPE html><html><body>${kaynak}</body></html>`, "text/html").body;

  const bloklar = [];
  const listeler = []; // open lists, innermost last: { ol, sayac }
  let tampon = [];     // runs of the paragraph being read
  let bekleyenIsaret = null; // set while reading an <li>: the first text it holds becomes the item

  const derinlik = () => listeler.length;

  /** Writes what has been collected as a paragraph / list item and starts a new run list. */
  function yayinla() {
    const parcalar = parcalariTopla(tampon);
    tampon = [];
    if (!parcalar.some((p) => !p.satirSonu)) return;
    if (bekleyenIsaret != null) {
      bloklar.push({ tur: "li", derinlik: derinlik() - 1, isaret: bekleyenIsaret, parcalar });
      bekleyenIsaret = null;
    } else {
      bloklar.push({ tur: "p", derinlik: derinlik(), parcalar });
    }
  }

  function gez(dugum, kalin, italik) {
    if (dugum.nodeType === 3) { tampon.push({ metin: dugum.nodeValue, kalin, italik }); return; }
    if (dugum.nodeType !== 1) return;
    const ad = dugum.nodeName.toLowerCase();
    if (YOK_SAY.has(ad)) return;
    if (ad === "br") { tampon.push({ satirSonu: true }); return; }
    if (ad === "hr") { yayinla(); bloklar.push({ tur: "hr" }); return; }
    const k = kalin || KALIN.has(ad);
    const i = italik || ITALIK.has(ad);
    if (ad === "ol" || ad === "ul") {
      yayinla();
      const baslangic = Number.parseInt(dugum.getAttribute("start"), 10);
      listeler.push({ ol: ad === "ol", sayac: Number.isFinite(baslangic) ? baslangic - 1 : 0 });
      for (const cocuk of dugum.childNodes) gez(cocuk, k, i);
      yayinla();
      listeler.pop();
      return;
    }
    if (ad === "li") {
      yayinla();
      const liste = listeler[listeler.length - 1];
      if (!liste) { // an <li> outside a list: a plain paragraph
        for (const cocuk of dugum.childNodes) gez(cocuk, k, i);
        yayinla();
        return;
      }
      liste.sayac += 1;
      bekleyenIsaret = liste.ol ? `${liste.sayac}.` : (derinlik() > 1 ? "–" : "•");
      for (const cocuk of dugum.childNodes) gez(cocuk, k, i);
      yayinla();
      bekleyenIsaret = null;
      return;
    }
    if (KALIN.has(ad) || ITALIK.has(ad) || SATIR_ICI.has(ad) || ad.includes(":")) {
      for (const cocuk of dugum.childNodes) gez(cocuk, k, i);
      return;
    }
    // p, div, a heading, a table cell, anything else: its text is a paragraph (a heading in bold)
    yayinla();
    for (const cocuk of dugum.childNodes) gez(cocuk, k || BASLIK_TAG.test(ad), i);
    yayinla();
  }

  for (const cocuk of govde.childNodes) gez(cocuk, false, false);
  yayinla();

  // a rule at the start / end / twice in a row splits nothing
  return bloklar.filter((b, n) => b.tur !== "hr" || (n > 0 && n < bloklar.length - 1 && bloklar[n - 1].tur !== "hr"));
}

/** The blocks cut at every rule: [[block, ...], [block, ...]]; no rule = one section. Always at least one (maybe empty) section. */
export function bolumlereAyir(bloklar) {
  const bolumler = [[]];
  for (const b of bloklar || []) {
    if (b.tur === "hr") bolumler.push([]);
    else bolumler[bolumler.length - 1].push(b);
  }
  return bolumler.filter((b, n) => b.length || n === 0);
}
